import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './VetoFlow.css';

/** Hur länge ett val får vara obekräftat innan "inte sparat" visas (ms). */
const UNSAVED_AFTER_MS = 2000;
/** Väntan mellan nya försök när ett val inte gick fram (ms). */
const RETRY_DELAY_MS = 1000;

/**
 * Har spelaren valt allt omgången kräver? Speglar backendens
 * `_is_complete_selection` (ban = count kartor, pick = 1, side = en sida).
 *
 * @param {string} roundType - 'ban' | 'pick' | 'side'
 * @param {number} roundCount - antal kartor som ska bannas
 * @param {string[]|string|null} selection - kartlista eller 'ct'/'t'
 * @returns {boolean} true om valet är komplett
 */
function isComplete(roundType, roundCount, selection) {
  if (roundType === 'side') return selection === 'ct' || selection === 't';
  if (!Array.isArray(selection)) return false;
  return selection.length >= (roundType === 'ban' ? roundCount : 1);
}

/**
 * Aktiv röstomgång. Varje klick är rösten: hela valet POST:as direkt med ett
 * löpnummer (seq) och kan ändras tills timern når 0 — servern stänger
 * omgången då och är enda sanningen för vem som valt vad.
 *
 * Det röstande laget ser lagkamraternas val live (antal + namn på kartan);
 * motståndare och åskådare ser bara vem som valt (bock/delvis).
 *
 * @param {object} props
 * @param {object} props.vetoState - publikt veto-tillstånd från servern
 * @param {number|string} props.matchId - matchens id
 * @param {object} props.teamSelections - lagets val {discord_id: [maps] | 'ct'/'t'} (bara till eget lag)
 * @param {number|null} props.initialSeq - mitt senast sparade löpnummer (efter reload)
 * @param {object|null} props.mapStrengths - kart-ELO-hints per karta
 * @returns {JSX.Element} omgångsvyn
 */
export default function VoteRound({ vetoState, matchId, teamSelections = {}, initialSeq = null, mapStrengths = null }) {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [selectedMaps, setSelectedMaps] = useState([]);
  const [selectedSide, setSelectedSide] = useState(null);
  const [unsaved, setUnsaved] = useState(false);
  const [error, setError] = useState(null);

  const currentRound = vetoState.rounds[vetoState.current_round];
  const roundIdx = vetoState.current_round;
  const roundType = currentRound?.type;
  const roundTeam = currentRound?.team;
  const roundCount = currentRound?.count || 1;
  const timerStart = currentRound?.timer_start;
  const timerDuration = currentRound?.timer_duration || 30;

  // Timer
  const secondsLeft = useCallback(() => (
    timerStart ? Math.max(0, Math.ceil(timerStart + timerDuration - Date.now() / 1000)) : timerDuration
  ), [timerStart, timerDuration]);
  const [timeLeft, setTimeLeft] = useState(secondsLeft);

  useEffect(() => {
    setTimeLeft(secondsLeft());
    if (!timerStart) return;
    const interval = setInterval(() => setTimeLeft(secondsLeft()), 250);
    return () => clearInterval(interval);
  }, [timerStart, secondsLeft]);

  const userDiscordId = user?.discord_id?.toString();
  const userTeam = useMemo(() => {
    if (!userDiscordId) return null;
    if (vetoState.team1_players?.includes(userDiscordId)) return 'team1';
    if (vetoState.team2_players?.includes(userDiscordId)) return 'team2';
    return null;
  }, [userDiscordId, vetoState.team1_players, vetoState.team2_players]);

  const isMyTurn = userTeam === roundTeam;
  const isCaptain = vetoState.veto_mode === 'captain';
  const isLeader = userDiscordId === vetoState[`${userTeam}_leader`];
  const canVote = isMyTurn && (!isCaptain || isLeader)
    && currentRound?.status === 'active' && timeLeft > 0;

  // ── Sändning: senaste valet köas, ett anrop i taget, retry vid fel ──
  const seqRef = useRef(initialSeq || 0);
  const roundRef = useRef(roundIdx);
  const touchedRef = useRef(false);       // har jag klickat i den här omgången?
  const pendingRef = useRef(null);        // { selection, version } som inte skickats än
  const inFlightRef = useRef(false);
  const versionRef = useRef(0);           // ökar per klick
  const confirmedRef = useRef(0);         // senaste version servern bekräftat
  const retryTimerRef = useRef(null);

  useEffect(() => {
    if (initialSeq) seqRef.current = Math.max(seqRef.current, initialSeq);
  }, [initialSeq]);

  // Ny omgång: nollställ allt lokalt
  useEffect(() => {
    roundRef.current = roundIdx;
    touchedRef.current = false;
    pendingRef.current = null;
    versionRef.current = 0;
    confirmedRef.current = 0;
    clearTimeout(retryTimerRef.current);
    setSelectedMaps([]);
    setSelectedSide(null);
    setUnsaved(false);
    setError(null);
  }, [roundIdx]);

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  // Efter reload: ta tillbaka mitt val från servern så länge jag inte klickat
  const serverMine = userDiscordId ? teamSelections[userDiscordId] : undefined;
  useEffect(() => {
    if (touchedRef.current || serverMine === undefined) return;
    if (roundType === 'side') setSelectedSide(serverMine || null);
    else setSelectedMaps(Array.isArray(serverMine) ? serverMine : []);
  }, [serverMine, roundType]);

  const flush = useCallback(async () => {
    if (inFlightRef.current || !pendingRef.current) return;
    const { selection, version } = pendingRef.current;
    pendingRef.current = null;
    inFlightRef.current = true;
    const round = roundRef.current;
    const seq = Math.max(Date.now(), seqRef.current + 1);
    seqRef.current = seq;

    let retry = false;
    try {
      const body = roundType === 'side'
        ? { side: selection || '', seq }
        : { maps: selection, seq };
      const response = await fetch(`${API_URL}/api/veto/${matchId}/vote`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.stale) {
          // Servern har ett nyare seq (t.ex. annan flik) — skicka igen ovanpå det.
          seqRef.current = Math.max(seqRef.current, data.seq || 0);
          retry = true;
        } else {
          confirmedRef.current = Math.max(confirmedRef.current, version);
          if (confirmedRef.current >= versionRef.current) setUnsaved(false);
        }
      } else if (response.status === 409 || response.status >= 500) {
        retry = true; // låset upptaget / tillfälligt fel
      } else if (round === roundRef.current) {
        // Permanent (t.ex. omgången stängd precis) — försök inte igen.
        const err = await response.json().catch(() => ({}));
        console.warn('[veto] vote rejected status=%d %o', response.status, err);
        if (response.status !== 400) setError(err.detail?.error || t('veto.voteFailedDefault'));
      }
    } catch (err) {
      retry = true; // nätverksfel
    } finally {
      inFlightRef.current = false;
    }

    if (round !== roundRef.current) return; // omgången är över — släpp allt
    if (retry && !pendingRef.current) pendingRef.current = { selection, version };
    if (pendingRef.current) {
      if (retry) retryTimerRef.current = setTimeout(() => flush(), RETRY_DELAY_MS);
      else flush();
    }
  }, [roundType, matchId, getAuthHeaders, t]);

  const persist = useCallback((selection) => {
    touchedRef.current = true;
    versionRef.current += 1;
    const version = versionRef.current;
    pendingRef.current = { selection, version };
    flush();
    setTimeout(() => {
      if (confirmedRef.current < version && versionRef.current === version) setUnsaved(true);
    }, UNSAVED_AFTER_MS);
  }, [flush]);

  const toggleMap = (tag) => {
    if (!canVote) return;
    let next;
    if (selectedMaps.includes(tag)) next = selectedMaps.filter(m => m !== tag);
    else if (roundType === 'pick') next = [tag];
    else if (selectedMaps.length >= roundCount) return;
    else next = [...selectedMaps, tag];
    setSelectedMaps(next);
    persist(next);
  };

  const chooseSide = (side) => {
    if (!canVote) return;
    const next = selectedSide === side ? null : side;
    setSelectedSide(next);
    persist(next);
  };

  // ── Visning ──
  const playerNames = useMemo(() => vetoState.player_names || {}, [vetoState.player_names]);
  const activePlayerIds = vetoState[`${roundTeam}_players`] || [];
  const totalPlayers = activePlayerIds.length || 5;

  // Eget lag: serverns val med mitt eget lokala val ovanpå (syns direkt).
  const mergedSelections = useMemo(() => {
    if (!isMyTurn) return {};
    const merged = { ...teamSelections };
    if (userDiscordId && touchedRef.current) {
      const mine = roundType === 'side' ? selectedSide : selectedMaps;
      const empty = roundType === 'side' ? !mine : mine.length === 0;
      if (empty) delete merged[userDiscordId];
      else merged[userDiscordId] = mine;
    }
    return merged;
  }, [isMyTurn, teamSelections, userDiscordId, roundType, selectedSide, selectedMaps]);

  // Vem har valt: eget lag räknar från valen (färskast), övriga från publika status.
  const votedIds = isMyTurn ? Object.keys(mergedSelections) : (currentRound?.votes?._voted_ids || []);
  const completeIds = isMyTurn
    ? votedIds.filter(id => isComplete(roundType, roundCount, mergedSelections[id]))
    : (currentRound?.votes?._complete_ids || []);

  const activePlayers = activePlayerIds.map(id => ({
    id,
    name: playerNames[id] || id,
    status: completeIds.includes(id) ? 'complete' : (votedIds.includes(id) ? 'partial' : ''),
  }));

  // Karta/sida → namn på de i laget som valt den (jag som "Du")
  const votersFor = useMemo(() => {
    const result = {};
    for (const [discordId, selection] of Object.entries(mergedSelections)) {
      const name = discordId === userDiscordId ? t('veto.you') : (playerNames[discordId] || discordId);
      const picks = Array.isArray(selection) ? selection : [selection];
      for (const key of picks) {
        if (!result[key]) result[key] = [];
        result[key].push(name);
      }
    }
    return result;
  }, [mergedSelections, userDiscordId, playerNames, t]);

  const teamLabel = roundTeam === 'team1'
    ? (vetoState.team1_name || 'Team 1')
    : (vetoState.team2_name || 'Team 2');

  let roundAction = '';
  let roundDesc = '';
  if (roundType === 'ban') {
    roundAction = t('veto.actionBan');
    roundDesc = isMyTurn
      ? (roundCount === 1
          ? t('veto.banInstructionOne', { count: roundCount })
          : t('veto.banInstructionMany', { count: roundCount }))
      : t('veto.banOther', { team: teamLabel });
  } else if (roundType === 'pick') {
    roundAction = t('veto.actionPick');
    roundDesc = isMyTurn
      ? t('veto.pickMine')
      : t('veto.pickOther', { team: teamLabel });
  } else if (roundType === 'side') {
    roundAction = t('veto.actionSide');
    roundDesc = isMyTurn ? t('veto.sideMine') : t('veto.sideOther', { team: teamLabel });
  }

  if (isCaptain && isMyTurn && !isLeader) {
    roundDesc = t('veto.waitingCaptain');
  }

  const renderVoterChips = (key) => {
    const voters = votersFor[key] || [];
    if (voters.length === 0) return null;
    return (
      <div className="map-voters">
        {voters.map((name, i) => (
          <span key={i} className={`map-voter-tag ${roundType}`}>{name}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="vote-round">
      <div className="round-header">
        <h2 className={`round-title ${roundTeam}`}>
          {teamLabel} <span className={`round-action ${roundType}`}>{roundAction}</span>
        </h2>
        <div className="round-timer">
          <div
            className="timer-bar"
            style={{ width: `${(timeLeft / timerDuration) * 100}%` }}
          />
          <span className="timer-text">{timeLeft}s</span>
        </div>
      </div>

      {activePlayers.length > 0 && (
        <div className="round-players">
          {activePlayers.map(p => (
            <span
              key={p.id}
              className={`player-tag ${p.status === 'complete' ? 'ready' : ''} ${p.status === 'partial' ? 'partial' : ''}`}
              title={p.status === 'partial' ? t('veto.partialChoice') : undefined}
            >
              {p.status === 'complete' && <span className="player-check">{'✓'}</span>}
              {p.status === 'partial' && <span className="player-check">{'◐'}</span>}
              {p.name}
            </span>
          ))}
        </div>
      )}
      <p className="round-desc">{roundDesc}</p>
      {error && <div className="veto-error">{error}</div>}

      {/* Vote progress */}
      <div className="vote-progress">
        <span>{votedIds.length}/{totalPlayers} {t('veto.haveChosen')}</span>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(votedIds.length / totalPlayers) * 100}%` }}
          />
        </div>
      </div>

      {/* Side vote — det röstande laget ser knapparna (och vem som valt vad) */}
      {roundType === 'side' && isMyTurn && (
        <div className="side-vote">
          {['ct', 't'].map(side => (
            <button
              key={side}
              className={`side-btn ${side} ${selectedSide === side ? 'selected' : ''}`}
              onClick={() => chooseSide(side)}
              disabled={!canVote}
            >
              {renderVoterChips(side)}
              {(votersFor[side]?.length || 0) > 0 && (
                <span className="map-vote-count">{votersFor[side].length}</span>
              )}
              <img className="side-img" src={`/stats/images/maps/${side}.jpg`} alt={side.toUpperCase()} />
              <span className="side-label">{side.toUpperCase()}</span>
              <span className="side-desc">{side === 'ct' ? t('veto.ctName') : t('veto.tName')}</span>
            </button>
          ))}
        </div>
      )}

      {/* Map grid for ban/pick */}
      {roundType !== 'side' && (
        <div className="vote-map-grid">
          {vetoState.maps_remaining.map(tag => {
            const isSelected = selectedMaps.includes(tag);
            const mapInfo = vetoState.map_pool_info?.[tag];
            const imgExt = tag === 'de_train' ? 'png' : 'jpg';
            const voteCount = votersFor[tag]?.length || 0;
            // Kart-ELO-hint (fas 4C): visas bara vid tydlig skillnad (≥10 netto).
            const strength = mapStrengths?.[tag];
            const strengthTeam = strength && Math.abs(strength.diff) >= 10
              ? (strength.diff > 0
                ? { name: vetoState.team1_name, diff: strength.diff }
                : { name: vetoState.team2_name, diff: -strength.diff })
              : null;
            return (
              <div
                key={tag}
                className={`vote-map-card ${isSelected ? (roundType === 'ban' ? 'ban-selected' : 'pick-selected') : ''} ${!canVote ? 'disabled' : ''}`}
                onClick={() => toggleMap(tag)}
                style={{ backgroundImage: `url(/stats/images/maps/${tag}.${imgExt})` }}
              >
                {renderVoterChips(tag)}
                {voteCount > 0 && (
                  <span className={`map-vote-count ${roundType}`}>{voteCount}</span>
                )}
                <span className="vote-map-name">{mapInfo?.name || tag}</span>
                {strengthTeam && (
                  <span
                    className="vote-map-strength"
                    title={t('veto.mapStrengthHint')}
                  >
                    {'💪'} {strengthTeam.name} +{strengthTeam.diff}
                  </span>
                )}
                {isSelected && (
                  <span className="vote-map-icon">
                    {roundType === 'ban' ? '✕' : '✓'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selection hint */}
      {canVote && roundType !== 'side' && (
        <div className="selection-counter">
          {selectedMaps.length}/{roundType === 'pick' ? 1 : roundCount} {t('veto.selectedChangeable')}
        </div>
      )}

      {unsaved && (
        <div className="vote-unsaved">{t('veto.notSaved')}</div>
      )}
    </div>
  );
}
